// @flow
import React from "react";
import { compose, withState } from "recompose";
import ToothChart from "src/shared/ToothChart/ToothChart";
import styled, { css } from "react-emotion";
import { Collapse, Steps, Input, Anchor } from "antd";
import SubmissionWrapper from "./SubmissionWrapper";
import ViewFormQuestion from "./FormQuestion";
import { Content, Separator, stepCss, toothChartCss } from "./Shared";
import getSummary from "./BeginningVisit/summary";
import ICONScore from "./BeginningVisit/icon";
import Goals from "./BeginningVisit/Goals";
import Referral from "./Referral";
import { getSubmissionAnswer } from "./answers";

// TODO: FIX HARD CODED SECTIONS FOR BEGINNING VISIST
const beginningSections = [{ min: 0, max: 17 }, { min: 18, max: Infinity }];

const beginningSeparators = [
  "Profile Dx",
  "Which TMJ issues are present?",
  "Crossbite Dx",
  "Overbite",
  "Right Molar Relationship Class",
  "Right Canine Relationship Class",
  "Left Molar Relationship Class",
  "Left Canine Relationship Class",
  "Upper Arch Type Dx",
  "Lower Arch Type Dx",
  "Esth. Dx",
  "Additional Diagnosis",
  "Refer to",
  "Notes",
];

const modifiers = compose(withState("section", "setSection", 0));

const BeginningVisit = props => {
  const {
    submission,
    form,
    forms,
    sessionAnswers,
    section,
    setSection,
    teeth,
    editable,
    patient,
  } = props;

  const isSubmitted = !!submission.submitted_at;
  const iconResult = new ICONScore(patient, form, submission).result;

  const treatmentOptionForm = Object.values(forms).find(f => {
    return f.slug === "treatment_option";
  });

  return (
    <SubmissionWrapper {...props}>
      <Steps className={stepCss} current={section} progressDot>
        <Steps.Step
          title="TC"
          onClick={() => {
            setSection(0);
          }}
        />
        <Steps.Step
          title="Dr: Findings/Goals"
          onClick={() => {
            setSection(1);
          }}
        />
      </Steps>

      <Collapse bordered={false}>
        <Collapse.Panel header="Tooth chart" className={toothChartCss}>
          <ToothChart
            stagesVisible={["antecedent", "estimated", "rendered"]}
            previousProcedures={teeth}
            disabled={!editable}
            procedures={
              // If the form has been submitted, we can't edit the tooth chart.
              // So don't pass in toothData as editable procedures.
              !isSubmitted ? props.toothData : []
            }
            setProcedures={props.onToothChartUpdateAsync}
          />
        </Collapse.Panel>
      </Collapse>

      <MainContent>
        <div>
          <Content>
            {form.forms_questions.map((q, n) => {
              // Are we showing these questions in the section?
              const { min, max } = beginningSections[section];
              if (typeof min === "number" && n < min) {
                return null;
              }
              if (typeof max === "number" && n > max) {
                return null;
              }

              return [
                beginningSeparators.indexOf(q.question_text) > -1 && (
                  <Separator key={`sep-${n}`} />
                ),
                <ViewFormQuestion
                  patient={patient}
                  allAnswers={sessionAnswers.concat(submission.answers || [])}
                  submission={submission}
                  answer={getSubmissionAnswer({
                    answers: submission.answers,
                    questionID: q.id,
                    submissionID: submission.id,
                  })}
                  onChange={value => props.onAnswer(q.id, value)}
                  editable={editable}
                  key={q.id}
                  q={q}
                />,
              ].filter(Boolean);
            })}
          </Content>
          {section === 1 && (
            <div>
              <Content>
                {/* // IF we're showing the goals and results section, we want to show the ability to make
                // many goals for the treatment. */}
                <Goals patient={patient} beginningVisit={submission} />
              </Content>
              <Content>
                <Referral patient={patient} parentSubmission={submission} />
              </Content>
            </div>
          )}
          <Collapse bordered={false}>
            <Collapse.Panel
              header="Summary (auto-generated)"
              className={summaryCss}
            >
              <Input.TextArea
                autosize
                value={getSummary(
                  patient,
                  form,
                  submission,
                  treatmentOptionForm
                )}
              />
            </Collapse.Panel>
          </Collapse>
        </div>

        <ICON>
          <Anchor offsetTop={32}>
            <p>ICON details</p>
            <ol>
              <li>
                <span>ICON score:</span> <b>{iconResult.score}</b>{" "}
                {iconResult.missing.length > 0 && `(missing information)`}
              </li>
              <li>
                <span>Case complexity:</span> <b>{iconResult.complexity}</b>
              </li>
              <li>
                <span>Needs treatment:</span>{" "}
                <b>{iconResult.needs ? "Yes" : "No"}</b>
              </li>
              {iconResult.missing.length > 0 && (
                <li>
                  <span>Missing:</span>{" "}
                  <b>
                    {iconResult.missing.map(m => (
                      <Missing key={m}>{m}</Missing>
                    ))}
                  </b>
                </li>
              )}
            </ol>
          </Anchor>
        </ICON>
      </MainContent>
    </SubmissionWrapper>
  );
};

export const summaryCss = css`
  border: 0;
  background: #fff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.05);
  margin: 1.5rem 0 0;
`;

const MainContent = styled("div")`
  display: flex;

  > div:first-child {
    flex: 1;
  }
`;

const ICON = styled("div")`
  width: 200px;
  padding: 2rem 1rem;
  margin-left: 1rem;
  background: #fff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.05);

  ol,
  li {
    margin: 0;
    padding: 0;
  }

  li {
    margin-left: 1rem;
  }

  li + li {
    margin-top: 0.5rem;
  }

  .ant-affix {
    z-index: 0;
  }

  .ant-anchor-ink {
    display: none;
  }
`;

const Missing = styled("span")`
  display: block;
`;

export default modifiers(BeginningVisit);
